import { useEffect, useRef, useState } from 'react'
import DonutChart from "components/common/charts/DonutChart";
import "rc-slider/assets/index.css";
import allImgPaths from 'assets/images';
import useAppState from 'context/useAppState';
import { extractJSONFromText, jsonToMarkdown } from 'utils/function';
import { cloneDeep, isEqual } from 'lodash';
import { useParams } from 'react-router-dom';
import Toast from 'components/common/Toast';
import { getBudgetAllocationData, getDefaultBudgetValues, getRFPCategories } from 'services/event';
import { getLLMAgentRAGUpdatePayload } from 'services/llm/payloads';
import { ACTUAL_TASK_NAMES } from 'utils/constant';

const InputBudgetField = ({ categoryName, name, value, budgetPlaceholder, currency, onChange, onFocus, onBlur, index, readOnly }: any) => {
    return (
        <div className='flex-shrink-0'>
            <p className='text-sm text-[#344054] font-interMedium pb-1.5'>
                {categoryName}
            </p>
            <div className="w-[228px] border border-[#d0d5dd] rounded-lg flex items-center pl-3 bg-white hover:border-[#1E0AF5] focus-within:border-[#1E0AF5]">
                <span className='text-base text-[#344054]'>{currency}</span>
                <input
                    className="placeholder:text-base placeholder:font-interMedium text-base text-[#0C111D] font-interMedium w-full input-field border border-[#D0D5DD] border-none !pl-2"
                    type="text"
                    placeholder={budgetPlaceholder}
                    name={name}
                    value={value}
                    readOnly={readOnly}
                    onChange={(e: any) => onChange(index, e.target.name, e?.target.value)}
                />
            </div>
        </div>
    )
}

const initValues: any[] = [
    { category: 'Venue', key: 'venue', value: "", originalValue: 0, placeholder: "6,000", remaining: 0, percent: 15, color: '#FF0000' },
    { category: 'Food & Beverage', key: 'food_beverage', value: "", originalValue: 0, placeholder: "6,000", remaining: 0, percent: 10, color: '#00FF00' },
    { category: 'Decor', key: 'decor', value: "", originalValue: 0, placeholder: "4,500", remaining: 0, percent: 10, color: '#1E0AF4' },
    { category: 'Gifts', key: 'gifts', value: "", originalValue: 0, placeholder: "7,500", remaining: 0, percent: 5, color: '#FFFF00' },
    { category: 'Entertainment', key: 'entertainment', value: "", originalValue: 0, placeholder: "1,500", remaining: 0, percent: 10, color: '#FFA500' },
    { category: 'Guest speakers & Marketing', key: 'guest_speakers_marketing', value: "", originalValue: 0, placeholder: "2,000", remaining: 0, percent: 15, color: '#0000BB' },
    { category: 'Miscellaneous', key: 'miscellaneous', value: "", originalValue: 0, placeholder: "1,000", remaining: 0, percent: 10, color: '#FFC0CB' }
]
const totalBudgetInit = {
    category: 'Total Budget', key: 'total_budget', value: "", originalValue: 0, placeholder: "30,000", remaining: 0, color: '#D0D5DD'
}

const BudgetAllocation = ({ messageData }: any) => {
    const { id: eventId } = useParams();
    let { content: messageContent, _id: messageId } = messageData;
    const jsonDetails = extractJSONFromText(messageContent);
    let { content: budgetContent, taskId, isViewMode } = jsonDetails;
    let ragId = budgetContent?.ragId || null
    let newMessageId = budgetContent?.newMessageId || null
    const [showAddcategory, setShowAddcategory] = useState(false);
    const { setState: setEventDataState, eventFetchedData } = useAppState("eventData");
    const [formData, setFormData] = useState<any[]>([]);
    const [totalBudget, setTotalBudget] = useState<any>(totalBudgetInit);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newBudget, setNewBudget] = useState('');
    const [newBudgetAllocationError, setNewBudgetAllocationError] = useState(false)
    const [saveBtnDisable, setSaveBtnDisable] = useState(false)
    const [isDirty, setIsDirty] = useState(false);
    const [totalAllocated, setTotalAllocated] = useState(0)
    const [allocatedPercent, setAllocatedPercent] = useState(0)
    const [currentIndex, setCurrentIndex] = useState<any>(null)
    let clonedFormData = useRef<any>(cloneDeep([]));
    const { selectedCardDetail } = useAppState("cardWrapper");
    const { id, draftEventId } = useParams();
    const { draftEvent } = useAppState('newFeteChat')
    const { updateMessageContent } = useAppState('chatMessages');
    const { currentSession, updateRAGEntryOfFete } = useAppState('chatBox')
    const [initDefaultValues, setInitDefaultValues] = useState<any>([]);
    const [showWarning, setShowWarning] = useState(false)
    const hasFetchedData = useRef(false);
    const [rfpCategories, setRfpCategories] = useState([])
    const [inputCategoryName, setIinputCategoryName] = useState('');

    // To allocate default budget when creating for first time or resetting budget allocation
    useEffect(() => {
        if (!newMessageId && !isViewMode) {
            setupDefaultBudgetValues()
        }
    }, [])

    useEffect(() => {
        areArraysEqual(formData, clonedFormData.current)
    }, [formData])

    // To fetch current allocated budget data in View or Edit Mode
    useEffect(() => {
        if ((ragId || isViewMode) && !hasFetchedData.current) {
            fetchBudgetAllocationData()
            hasFetchedData.current = true;
            setShowWarning(false)
        }
    }, [newMessageId, isViewMode])

    const setupDefaultBudgetValues = () => {
        setRfpCategories([])
        const budget = eventFetchedData?.budget?.max;
        if (budget && budget > -1) {
            const updatedTotalBudget = cloneDeep(totalBudget);
            updatedTotalBudget.value = budget > 0 ? formatNumber(budget.toString()) : budget;
            updatedTotalBudget.originalValue = budget;
            setTotalBudget(updatedTotalBudget);
        }

        setDefaultValues(budget || 0)
    }

    const areArraysEqual = (original: any, cloned: any) => {
        const isSame = isEqual(original, cloned);
        setSaveBtnDisable(isSame)
        setEventDataState({ unsavedChanges: !isSame })
    };

    const fetchRFPCategories = async () => {
        const { data } = await getRFPCategories({ eventId })
        setRfpCategories(data)
        return data;
    }

    const fetchBudgetAllocationData = async () => {
        const categories = await fetchRFPCategories();
        let payload: any = { eventId }
        if (ragId) payload = { ...payload, ragId }
        if (isViewMode) payload = { ...payload, taskId }
        const { data } = await getBudgetAllocationData(payload)

        // console.log({ savedData: data })
        const budget = eventFetchedData?.budget?.max;
        const previousBudgetValue = data?.content?.totalBudgetValue || budget;

        // console.log({ budget, previousBudgetValue })

        if (data?.content?.items?.length) {
            // const updatedBudgetValue = budget >= previousBudgetValue ? data?.content?.items : refreshBudgetValues(budget, data.content.items)
            const updatedBudgetValue = data?.content?.items;

            updatedBudgetValue.map((ubv: any) => {
                const categoryIndex = categories.indexOf(ubv.category.toLowerCase());
                ubv['isRfpSent'] = categoryIndex > -1 ? true : false
                ubv['editTimeValue'] = ubv.originalValue
            })

            // setFormData(data?.content?.items)
            setFormData(updatedBudgetValue)

            clonedFormData.current = cloneDeep(updatedBudgetValue)

            const totalAllocated = updatedBudgetValue.reduce((a: any, b: any) => a + (+b.originalValue || 0), 0)
            setTotalAllocated(totalAllocated)
            setAllocatedPercent((totalAllocated / budget) * 100)

            const updatedTotalBudget = cloneDeep(totalBudget);
            updatedTotalBudget.value = budget > 0 ? formatNumber(budget.toString()) : budget;
            updatedTotalBudget.originalValue = budget;
            setTotalBudget(updatedTotalBudget);

            // Check if actual budget value has decreased, if so then show a message
            // const totalBudgetAllocated = data?.content?.totalAllocation || 0;
            // if (totalBudgetAllocated > budget) {
            //     setShowWarning(true)
            // }
        } else {
            setFormData([])
            setTotalAllocated(0)
            setAllocatedPercent(0)
        }
    }

    const refreshBudgetValues = (budget: any, items: any[]) => {
        let budgetItems = items;
        budgetItems = items.map(item => {
            // Calculate the new value based on the percentage of the budget
            const newOriginalValue = (item.percent / 100) * budget;

            // Round the original value to two decimal places, but keep it as a number
            const roundedOriginalValue = parseFloat(newOriginalValue.toFixed(2));

            // Format the value as a string, adding commas and decimals only if needed
            const formattedValue = formatNumberRefresh(roundedOriginalValue);

            return {
                ...item,
                originalValue: roundedOriginalValue, // Number type
                value: formattedValue // String type
            };
        });

        // console.log({ budgetItems })
        return budgetItems
    }

    const formatNumberRefresh = (value: number) => {
        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    const getEventId = () => {
        if (eventId) {
            return eventId;
        }
        if (draftEventId) {
            return draftEventId;
        }
        if (draftEvent && draftEvent?._id) {
            return draftEvent?._id;
        }
    }

    const setDefaultValues = async (budget: any) => {
        const { data } = await getDefaultBudgetValues(eventId);
        if (data?.length) {
            setFormData(data);
            data.map((dt: any) => dt['color'] = getRandomHexColor())
            clonedFormData.current = data
            calculate(data, budget || 0)
        }
    }


    const calculate = (data: any[], totalBudgetValue: number) => {
        const updatedData = data.map((item: any, index: any) => {
            const sharePerItem = Math.round(totalBudgetValue * (item.percent / 100));

            return {
                ...item,
                value: formatNumber(sharePerItem.toString()), // Ensure the value is a string
                originalValue: sharePerItem
            };
        });

        // if (!ragId && !isViewMode) {
        //     setFormData(updatedData)
        // }

        setFormData(updatedData)

        const totalAllocated = updatedData.reduce((a: any, b: any) => a + (+b.originalValue || 0), 0)
        setTotalAllocated(totalAllocated)
        setAllocatedPercent((totalAllocated / totalBudgetValue) * 100)

        setInitDefaultValues(updatedData)
    }

    const handleShowcategory = () => {
        setShowAddcategory(!showAddcategory);
        setNewCategoryName('')
        setNewBudget('')
        setCurrentIndex(null)
        setNewBudgetAllocationError(false)
    }

    const handleTotalBudgetChange = (index: number, name: any, value: any) => {
        let newValue = value.replace(/,/g, '')
        if (+newValue > 0) {
            const updatedTotalBudget = cloneDeep(totalBudget);
            updatedTotalBudget.value = formatNumber(newValue.toString())
            updatedTotalBudget.originalValue = +newValue;
            setTotalBudget(updatedTotalBudget)

            updateState(formData, formData)
        }
        return
    }

    const handleInputChange = (index: number, name: any, inputVal: any) => {
        setCurrentIndex(index)
        let newValue = inputVal.replace(/,/g, ''); // Remove existing commas

        const numValue = +newValue;  // To avoid trailing 0 in text box

        // If alphabet is entered, then set the current value as is
        if (isNaN(numValue)) {
            const originalValue = formData[index].value
            const originalDataValue = formData[index].originalValue
            setFormData(prevState => {
                const newState = [...prevState];
                newState[index].value = originalValue || '';
                newState[index].originalDataValue = originalDataValue;
                return newState;
            });
            return
        }

        // If no value, then set as 0
        if (!newValue || !isNaN(numValue) && numValue === 0) {
            const zeroValue = !newValue ? '' : 0
            setFormData(prevState => {
                const newState = cloneDeep(prevState);
                newState[index].value = zeroValue;
                newState[index].originalValue = zeroValue;
                updateState(prevState, newState)
                // checkIfDirty(newState)
                return newState;
            });
            return;
        }

        newValue = numValue.toString()
        const cleanedValue = newValue.replace(/\D/g, '');
        const regex = /^[0-9]*$/; // Allow only numbers

        if (regex.test(cleanedValue)) {
            const formattedValue = formatNumber(cleanedValue);
            const oldState = cloneDeep(formData);
            const newState = cloneDeep(formData)
            newState[index].value = formattedValue;
            newState[index].originalValue = numValue;
            newState[index].percent = (numValue / totalBudget.originalValue) * 100;

            updateState(oldState, newState)
        }

        // If this category value is decreased, show warning messages
        if (formData[index]['isRfpSent'] && numValue < formData[index]['editTimeValue']) {
            setIinputCategoryName(formData[index]['category'])
        } else {
            setIinputCategoryName('')
        }
    }

    const formatNumber = (number: any) => {
        return number.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const updateState = (oldData: any[], updatedData: any[]) => {
        const totalAllocated = (updatedData || []).reduce((a, b, index) => a + (+b.originalValue || 0), 0) || 0;
        if (totalAllocated > totalBudget.originalValue) {
            setFormData(oldData)
            return
        }
        setTotalAllocated(totalAllocated)
        setAllocatedPercent((totalAllocated / totalBudget.originalValue) * 100)
        setFormData(updatedData);

        // checkIfDirty(updatedData)
    }

    const removeCategory = (index: number) => {
        const updatedData = formData.filter((item, idx) => idx !== index);
        setFormData(updatedData)
        // checkIfDirty(updatedData);

        const totalAllocated = (updatedData || []).reduce((a, b) => a + (+b.originalValue || 0), 0) || 0
        setTotalAllocated(totalAllocated)
        setAllocatedPercent((totalAllocated / totalBudget.originalValue) * 100)

        setCurrentIndex(null)
    };

    const addCategory = () => {
        if (!newCategoryName) return

        const percent = (+newBudget / totalBudget.originalValue) * 100;
        const newCategory = {
            category: newCategoryName,
            key: newCategoryName.toLowerCase().replace(/\s+/g, '_'),
            value: newBudget,
            originalValue: +newBudget,
            placeholder: '1,000',
            remaining: 0,
            percent,
            color: getRandomHexColor()
        };

        const updatedData = [...formData, newCategory];
        setFormData(updatedData)
        // checkIfDirty(updatedData);

        setNewCategoryName('');
        setNewBudget('')
        setShowAddcategory(!showAddcategory);
        setNewBudgetAllocationError(false);

        const totalAllocated = (updatedData || []).reduce((a, b) => a + (+b.originalValue || 0), 0) || 0
        setTotalAllocated(totalAllocated)
        setAllocatedPercent((totalAllocated / totalBudget.originalValue) * 100)
    };

    const getRandomHexColor = () => {
        const letters = '0123456789ABCDEF';
        let color = '#';
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        return color;
    }

    const checkIfDirty = (newState: any) => {
        const savedState: boolean = JSON.stringify(newState) !== JSON.stringify(clonedFormData.current)
        setIsDirty(savedState);
        setEventDataState({ unsavedChanges: savedState })
    };

    const formatNumberToK = (value: number): string => {
        if (value >= 1000) {
            const formattedValue = value / 1000;
            return formattedValue.toFixed(value % 1000 !== 0 ? 2 : 0) + 'k';
        }
        return value.toString();
    }

    const setNewBudgetValue = (value: any) => {
        const remainAmount = totalBudget.originalValue - totalAllocated;
        if (+value > remainAmount) {
            setNewBudgetAllocationError(true);
            setNewBudget(remainAmount.toString())
            return
        }
        setNewBudget(value)
        setNewBudgetAllocationError(false);
    }

    const save = async () => {
        setIsDirty(false)
        setSaveBtnDisable(false)
        setCurrentIndex(null)
        setNewBudgetAllocationError(false)

        if (taskId == 'taskId') taskId = null
        const currentTaskId = taskId ? taskId : selectedCardDetail?.['tasks'].find((tsk: any) => tsk.title.toLowerCase() == ACTUAL_TASK_NAMES.BUDGET.ALLOCATION.toLowerCase())?._id;

        const currentTeam = JSON.parse(localStorage.getItem('selectedTeam') as string)
        const isDraft = draftEventId ? true : false;

        // Only for saving data 
        const updatedFormData = cloneDeep(formData);
        updatedFormData.map((ufd: any) => {
            delete ufd?.isRfpSent;
            delete ufd?.editTimeValue;
        })

        const totalAllocation = (formData || []).reduce((total, item) => total += item.originalValue, 0);

        const messagePayload = {
            content: JSON.stringify({ 'isBudgetAllocation': true, 'items': updatedFormData, 'totalBudgetValue': totalBudget.originalValue, 'totalAllocation': totalAllocation }),
            teamId: currentTeam?.currentTeamId,
            eventId: getEventId(),
            is_draft: isDraft,
            taskId: currentTaskId,  // Draft Invitation Task
            cardId: selectedCardDetail?._id,
            blockId: selectedCardDetail?.columnId || selectedCardDetail?.block_id
        }

        // console.log({ messagePayload })

        if (!messagePayload.taskId) {
            Toast.error('', 'Task Id not available')
            setSaveBtnDisable(false)
            return
        }

        // Check for same data
        clonedFormData.current = cloneDeep(formData)
        areArraysEqual(formData, cloneDeep(formData))

        setEventDataState({ unsavedChanges: false })

        try {
            await updateMessageContent(messageId, messagePayload)
        } catch (error) {
            handleApiCases(false)
        }

        await handleRAGCall({ content: formData })
        // setSaveBtnDisable(false)
        handleApiCases(true)
    }

    const handleRAGCall = async ({ content }: any) => {
        const userData = JSON.parse(localStorage.getItem('userData') as string)
        const userEmail = localStorage.getItem('userEmail')
        const currentTeam = JSON.parse(localStorage.getItem('selectedTeam') as string)

        let ragPayload: any = {
            // session_id: currentSession?._id,
            text_content: jsonToMarkdown({ content: content, isBudgetAllocation: true }),
            text_prefix: 'Budget Allocation',
        }

        if (taskId == 'taskId') taskId = null
        const currentTaskId = taskId ? taskId : selectedCardDetail?.['tasks'].find((tsk: any) => tsk.title == 'Budget Allocation')?._id;

        const ragMetaData = {
            userId: userData?.id,
            userEmail,
            teamId: currentTeam?.currentTeamId,
            eventId: getEventId(),
            is_draft: false,
            taskId: currentTaskId,
            updatedAt: new Date().toISOString(),
            cardId: selectedCardDetail?._id
        }

        if (currentSession?._id) {
            ragPayload['session_id'] = currentSession._id
        }

        ragPayload = { ...ragPayload, document_path: messageId, additional_metadata: ragMetaData }

        try {
            await updateRAGEntryOfFete(getLLMAgentRAGUpdatePayload(ragPayload))
        } catch (error) {
            handleApiCases(false)
        }
    }

    const handleApiCases = (isSuccess: boolean) => {
        if (isSuccess) {
            Toast.success('', 'Saved successfully')
        } else {
            Toast.error('', 'Failed to save')
        }
    }

    // const reset = () => {
    //     const initData = clonedFormData.current;
    //     const budget = eventFetchedData?.budget?.max;
    //     setFormData(initData)
    //     const totalAllocated = initData.reduce((a: any, b: any) => a + (+b.originalValue || 0), 0)
    //     setTotalAllocated(totalAllocated)
    //     setAllocatedPercent((totalAllocated / budget) * 100)
    // }

    // const reset = () => {
    //     const initData = initDefaultValues;
    //     console.log({ initData })
    //     const budget = eventFetchedData?.budget?.max;
    //     setFormData(initData)
    //     const totalAllocated = initData.reduce((a: any, b: any) => a + (+b.originalValue || 0), 0)
    //     setTotalAllocated(totalAllocated)
    //     setAllocatedPercent((totalAllocated / budget) * 100)
    //     setEventDataState({ unsavedChanges: true })
    // }

    const reset = () => {
        setupDefaultBudgetValues()
        setEventDataState({ unsavedChanges: true })
    }

    const getRemainingBudget = () => {
        return (totalBudget.originalValue - totalAllocated).toLocaleString()
    }

    const getMaxAllowedValue = (categoryOriginalValue: number) => {
        return ((totalBudget.originalValue - totalAllocated) + categoryOriginalValue).toLocaleString()
    }

    return (
        <>
            <div className="pl-14">
                {/* {
                    showWarning &&
                    <h1 className="mb-5 text-red-500 text-xl font-bold">
                        The total allocation is greater than the current budget value
                    </h1>
                } */}

                {/* Heading */}
                <h2 className='text-lg text-[#101828] font-interSemiBold pb-10'>Budget Allocation</h2>

                {/* Budget Allocation */}
                <div className="flex border border-[#EAECF0] rounded bg-[#F9FAFB] py-6 px-6 items-center mb-11 max-w-[590px]">
                    <div className="w-1/2">
                        {/* <div className='flex mb-6'>
                            <InputBudgetField
                                categoryName={totalBudget.category}
                                budgetPlaceholder={totalBudget.placeholder}
                                currency='USD'
                                value={totalBudget.value}
                                name={totalBudget.key}
                                index={null}
                                readOnly={true}
                                onChange={handleTotalBudgetChange}
                            />
                        </div> */}
                        <div className="flex">
                            <div className="flex-shrink-0 pl-7">
                                <p className="text-base text-[#344054] font-interMedium pb-0.5">
                                    Total Budget
                                </p>
                                <div className="w-[228px] flex items-center bg-[#F9FAFB]">
                                    <span className="text-4xl text-[#0C111D] font-interSemiBold">$</span>
                                    <input
                                        className="text-4xl text-[#0C111D] font-interSemiBold w-full input-field border border-[#D0D5DD] border-none bg-[#F9FAFB] !pl-2"
                                        type="text"
                                        name={totalBudget.key}
                                        readOnly={true}
                                        defaultValue={totalBudget.value}
                                    />
                                </div>
                            </div>
                        </div>

                    </div>

                    <div className="w-1/2 flex justify-center -mb-2">
                        <DonutChart
                            showLegends={false}
                            colors={[totalBudget.color, ...(formData?.map((fd: any) => fd.color) || [])]}
                            series={[(+(totalBudget.originalValue - totalAllocated).toFixed(2) || 0), ...(formData?.map((fd: any) => +fd.originalValue) || [])]}
                            labels={['Remaining', ...(formData?.map((fd: any) => fd.category) || [])]}
                            height="250"
                            width="270"
                        />
                    </div>
                </div>

                {/* Budget Utilization */}
                <div className="flex max-w-[590px] gap-x-8">
                    {/* <div className="w-[calc(100%_-_280px)]"> */}
                    <div className="">
                        {
                            formData && formData.length > 0 && formData.map((item: any, index: number) => (
                                <div className="" key={index}>
                                    <div className="flex items-center mb-4 gap-x-6">
                                        <InputBudgetField
                                            categoryName={item.category}
                                            budgetPlaceholder={item.placeholder}
                                            currency='USD'
                                            value={item.value}
                                            name={item.key}
                                            index={index}
                                            readOnly={isViewMode}
                                            onChange={handleInputChange}
                                        />

                                        {
                                            !isViewMode &&
                                            <span className="mt-6">
                                                <img className='w-6 h-6 cursor-pointer flex-shrink-0 hover:bg-[#FEE4E2] rounded p-0.5' onClick={(e: any) => removeCategory(index)} src={allImgPaths.deleteIcon4} alt="" />
                                            </span>
                                        }
                                    </div>
                                    {
                                        currentIndex === index &&
                                        <div className='mb-3 -mt-3'>
                                            <p className='text-[#667085] text-sm font-interRegular'>
                                                {/* Remaining: ${(totalBudget.originalValue - totalAllocated).toLocaleString()} */}
                                                Remaining: ${getRemainingBudget()}
                                            </p>
                                            <div className="flex items-start gap-x-2 mt-4">
                                                <img className="relative -mt-2" src={allImgPaths.warningToastIcon} alt="" />

                                                <div className="">
                                                    <p className="text-[#475467] text-sm">
                                                        Max. available budget for adjustment is $ {getMaxAllowedValue(item.originalValue)}.
                                                        For further increase contact the fete owner
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                    {
                                        inputCategoryName && inputCategoryName.toLowerCase() == item.category.toLowerCase() &&
                                        <div className="flex items-start gap-x-2 mt-4 mb-2">
                                            <img className="relative -mt-2" src={allImgPaths.warningToastIcon} alt="" />

                                            <div className="">
                                                <p className="text-[#475467] text-sm">
                                                Contracts were signed under earlier pricing conditions. Reducing the budget may have an effect.
                                                </p>
                                            </div>
                                        </div>
                                    }
                                </div>
                            ))
                        }
                        <div className={`${showAddcategory ? "bg-[#F9FAFB] border border-[#F2F4F7] rounded-lg py-3 px-6" : ""}`}>
                            <div className={`${showAddcategory ? "" : "hidden"}`}>
                                <p className='text-sm text-[#344054] font-interMedium pb-2'>Enter Category Name</p>
                                <div className="flex items-center gap-x-5">
                                    <div className="flex-shrink-0 border border-[#d0d5dd] rounded-lg flex items-center w-[228px] hover:border-[#1E0AF5] focus-within:border-[#1E0AF5]">
                                        <input
                                            type="text"
                                            name="newCategory"
                                            className="placeholder:text-[#98A2B3] placeholder:text-base placeholder:font-interRegular text-base text-[#0C111D] font-interMedium w-full input-field border border-[#D0D5DD] border-none"
                                            placeholder="Category Name"
                                            value={newCategoryName}
                                            onChange={(e) => setNewCategoryName(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <p className='mt-4 text-sm text-[#344054] font-interMedium pb-2'>Enter Budget</p>
                                <div className="w-[228px] border border-[#d0d5dd] rounded-lg flex items-center pl-3 hover:border-[#1E0AF5] focus-within:border-[#1E0AF5] ">
                                    <span className='text-base text-[#344054]'>USD</span>
                                    <input
                                        className="placeholder:text-base placeholder:font-interMedium text-base text-[#0C111D] font-interMedium w-full input-field border border-[#D0D5DD] border-none !pl-2"
                                        type="number"
                                        name="newBudget"
                                        value={newBudget}
                                        onChange={(e: any) => setNewBudgetValue(e.target.value)}
                                    />
                                </div>
                                {
                                    newBudgetAllocationError &&
                                    <span className='pt-3 text-[red]'>Total allocation should not exceed ${(totalBudget.originalValue - totalAllocated).toFixed(2)}</span>
                                }
                            </div>
                        </div>
                        {/* Add Category Button */}
                        {
                            !isViewMode &&
                            <div className={`flex mt-9 ${showAddcategory ? 'w-[228px]' : ''}`}>
                                <button
                                    onClick={handleShowcategory}
                                    className={`rounded-md font-interMedium !px-3 !py-2 whitespace-nowrap border border-[#A59DFB] text-[#1e0af5] flex gap-x-1.5 justify-center items-center hover:bg-[#E8E6FE] ${showAddcategory ? 'w-auto flex-1' : 'w-full'}`}
                                >
                                    {showAddcategory ? '' : <img src={allImgPaths.plusCircleBlueIcon} alt="" />}
                                    {showAddcategory ? 'Cancel' : 'Add category'}
                                </button>
                                {showAddcategory &&
                                    <button
                                        className={`ml-5 submit-btn-blue !px-3 !py-2 whitespace-nowrap flex gap-x-1.5 items-center flex-1 justify-center hover:bg-[#120693] ${!newCategoryName ? 'button-disabled' : ''}`}
                                        onClick={addCategory}
                                        disabled={!newCategoryName}
                                    >
                                        Add
                                    </button>
                                }
                            </div>
                        }
                    </div>
                    <div className="w-[280px] border border-[#EAECF0] rounded p-6">
                        <div className="text-center mb-8">
                            <p className="text-base text-[#475467] pb-3">Budget Utilization</p>
                            <p className="text-[#079455] text-4xl font-interSemiBold">
                                {allocatedPercent.toFixed(2) || 0}%
                            </p>
                        </div>

                        <div className="text-center mt-2">
                            <p className="text-base text-[#475467] pb-3">Current balance</p>
                            <p className="text-[#101828] text-4xl font-interSemiBold pb-1">
                                ${(totalBudget.originalValue - totalAllocated).toLocaleString() || 0}
                            </p>
                            <p className="text-base font-interRegular text-[#101828]">
                                (${totalAllocated.toLocaleString()} used)
                            </p>
                        </div>
                    </div>
                </div>

                {
                    !isViewMode &&
                    <div className="flex justify-end mt-2 mr-10 gap-x-3">
                        <button
                            onClick={reset}
                            className='text-[#344054] font-interSemiBold border border-[#D0D5DD] rounded-lg tracking-wide py-2.5 px-4 !text-base hover:bg-[#f2f4f7]'>
                            Reset
                        </button>
                        <button
                            onClick={save}
                            className={`text-sm text-white bg-[#1E0AF5] hover:bg-[#120693] font-interMedium rounded-lg px-4 py-2 ${saveBtnDisable ? 'button-disabled' : ''}`}
                            disabled={saveBtnDisable}
                        >
                            Save Budget
                        </button>
                    </div>
                }
            </div>
        </>
    )
}

export default BudgetAllocation